import { useRef, useCallback } from "react";

/**
 * Maps each ISOB form field to its corresponding SharePoint list.
 * Lists: isob, isobBp, comp, drw, pgCommercial, dealerSac, advance, ld, misc
 */
const FIELD_TO_LIST_MAP = {
  // isob (main ISOB record)
  PO_number: "isob", POLOIDate: "isob", DAPTechApprovalDate: "isob",
  BOM_type: "isob", WHType: "isob", SKU_stored: "isob", DocumentExchangePlace: "isob",
  RelatedToPastSupply: "isob", PastSONo: "isob",
  scopeType: "isob", scopeText: "isob", partOrder: "isob",
  timelineOtherInformation: "isob",

  // isobBp (Sold To fields)
  BPCode: "isobBpSoldTo", BPName: "isobBpSoldTo", BPGSTN: "isobBpSoldTo", CIN: "isobBpSoldTo",
  BPTAN: "isobBpSoldTo", Location: "isobBpSoldTo", Pincode: "isobBpSoldTo", BPAddress: "isobBpSoldTo",
  ContactName: "isobBpSoldTo", Mobile: "isobBpSoldTo", Email: "isobBpSoldTo",
  Designation: "isobBpSoldTo", AddressCode: "isobBpSoldTo", Segment: "isobBpSoldTo",
  // isobBp (Ship To fields)
  Ship_BPCode: "isobBpShipTo", Ship_BPName: "isobBpShipTo", Ship_BPGSTN: "isobBpShipTo",
  Ship_CIN: "isobBpShipTo", Ship_BPTAN: "isobBpShipTo", Ship_Location: "isobBpShipTo",
  Ship_Pincode: "isobBpShipTo", Ship_BPAddress: "isobBpShipTo",
  Ship_ContactName: "isobBpShipTo", Ship_Mobile: "isobBpShipTo", Ship_Email: "isobBpShipTo",
  Ship_Designation: "isobBpShipTo", Ship_AddressCode: "isobBpShipTo",

  // comp (array - tracked separately)
  components: "comp",

  // drw (array - tracked separately)
  drawings: "drw",

  // pgCommercial (array + summary fields)
  pgDetails: "pgCommercial",
  Overall_Outflow: "pgCommercial", FinalBasicPrice: "pgCommercial",
  supplyType: "pgCommercial", serviceType: "pgCommercial",
  OrderValueLakhs: "pgCommercial",

  // dealerSac
  DealerSAC_applicable: "dealerSac", Dealer_SAC: "dealerSac",
  DealerSAC_value: "dealerSac", DealerCode: "dealerSac",
  Order_closing: "dealerSac", enquiryGeneration: "dealerSac",
  paymentCollection: "dealerSac", installation: "dealerSac",
  creditSharing: "dealerSac", Sharing_Branch: "dealerSac",
  Sharing_value: "dealerSac", SM: "dealerSac",

  // advance
  Advance: "advance", againstDeliveryPercent: "advance",
  againstInstallationPercent: "advance", installationCharge: "advance",

  // ld
  LD_applicable: "ld", LD_min: "ld", ldMax: "ld",
  deliveryStartDate: "ld", deliveryLDPerWeek: "ld",
  deliveryEndDate: "ld", deliveryMaxLD: "ld",
  instalStartDate: "ld", instalLDPerWeek: "ld",
  instalEndDate: "ld", instalMaxLD: "ld",

  // misc
  inspectionReqd: "misc", inspectionAt: "misc", inspectionAgency: "misc",
  inspContactName: "misc", inspContactMobile: "misc", inspContactAddress: "misc",
  supplyRemarks: "misc", siteContactPerson: "misc", siteContactMobile: "misc",
  logisticsRemarks: "misc", miscInstallationBy: "misc",
  processCheckingVisitToFactory: "misc", scopeOfPowerSupply: "misc",
  scopeOfExistingRacksRemoval: "misc", scopeOfUnloading: "misc",
  multipleFloorDetails: "misc", projectSiteRegulatoryAct: "misc",
  miscInstallationRemarks: "misc", miscQap: "misc", miscLoadTest: "misc",
  miscSiteDimensionReconfirmed: "misc", miscSiteReadinessForInstallation: "misc",
  miscVisualInspectionOfFloor: "misc", miscExistingRacksToBeRemoved: "misc",
  miscWorkingWarehouse: "misc", miscMultipleFloors: "misc",
  miscCoveredSpaceAvailable: "misc",
};

/**
 * Hook to track which SharePoint lists have been modified in the ISOB form.
 * 
 * For array-based lists (comp, drw, pgCommercial), it also tracks
 * individual record-level changes (added/updated/deleted).
 * 
 * Usage:
 *   const { markDirty, markArrayChange, getDirtyLists, getArrayChanges, resetTracker } = useIsobChangeTracker();
 */
export default function useIsobChangeTracker() {
  // Set of dirty list names
  const dirtyLists = useRef(new Set());

  // For array lists: { comp: { added: [...], updated: [...], deleted: [...] }, ... }
  const arrayChanges = useRef({
    comp: { added: [], updated: [], deleted: [] },
    drw: { added: [], updated: [], deleted: [] },
    pgCommercial: { added: [], updated: [], deleted: [] },
  });

  // Snapshot of original array data (set once when data is fetched)
  const originalArrays = useRef({
    comp: null,
    drw: null,
    pgCommercial: null,
  });

  /**
   * Mark a field (or list) as dirty based on field name.
   * Handles __bulk__ updates by marking all fields in the bulk object.
   */
  const markDirty = useCallback((fieldName, bulkValue) => {
    if (fieldName === "__bulk__" && bulkValue) {
      Object.keys(bulkValue).forEach((key) => {
        const list = FIELD_TO_LIST_MAP[key];
        if (list) dirtyLists.current.add(list);
      });
    } else {
      const list = FIELD_TO_LIST_MAP[fieldName];
      if (list) dirtyLists.current.add(list);
    }
  }, []);

  /**
   * Directly mark a specific list as dirty.
   */
  const markListDirty = useCallback((listName) => {
    dirtyLists.current.add(listName);
  }, []);

  /**
   * Set the original snapshot for an array list (call once after fetch).
   */
  const setOriginalArray = useCallback((listName, data) => {
    if (originalArrays.current.hasOwnProperty(listName)) {
      originalArrays.current[listName] = JSON.parse(JSON.stringify(data || []));
    }
  }, []);

  /**
   * Compute array-level changes by diffing current vs original.
   * Call this at submit time with the current array data.
   * 
   * For comp/drw/pgCommercial, compares by index and detects:
   * - added: items in current but not in original (extra indices)
   * - updated: items at same index but with different values
   * - deleted: items in original but not in current (missing indices)
   */
  const computeArrayChanges = useCallback((listName, currentData) => {
    const original = originalArrays.current[listName];
    if (!original) {
      // No original = all are new additions
      arrayChanges.current[listName] = {
        added: currentData || [],
        updated: [],
        deleted: [],
      };
      return;
    }

    const current = currentData || [];
    const added = [];
    const updated = [];
    const deleted = [];

    // Check updates and additions
    current.forEach((item, idx) => {
      if (idx < original.length) {
        if (JSON.stringify(item) !== JSON.stringify(original[idx])) {
          updated.push({ index: idx, data: item, originalData: original[idx] });
        }
      } else {
        added.push(item);
      }
    });

    // Check deletions (items in original that are beyond current length)
    if (original.length > current.length) {
      for (let i = current.length; i < original.length; i++) {
        deleted.push({ index: i, data: original[i] });
      }
    }

    arrayChanges.current[listName] = { added, updated, deleted };
  }, []);

  /**
   * Get the set of dirty list names.
   */
  const getDirtyLists = useCallback(() => {
    return new Set(dirtyLists.current);
  }, []);

  /**
   * Get array changes for a specific list.
   */
  const getArrayChanges = useCallback((listName) => {
    return arrayChanges.current[listName] || { added: [], updated: [], deleted: [] };
  }, []);

  /**
   * Check if a specific list is dirty.
   */
  const isListDirty = useCallback((listName) => {
    return dirtyLists.current.has(listName);
  }, []);

  /**
   * Reset the tracker (e.g., after successful submit or on form reset).
   */
  const resetTracker = useCallback(() => {
    dirtyLists.current = new Set();
    arrayChanges.current = {
      comp: { added: [], updated: [], deleted: [] },
      drw: { added: [], updated: [], deleted: [] },
      pgCommercial: { added: [], updated: [], deleted: [] },
    };
  }, []);

  return {
    markDirty,
    markListDirty,
    setOriginalArray,
    computeArrayChanges,
    getDirtyLists,
    getArrayChanges,
    isListDirty,
    resetTracker,
    FIELD_TO_LIST_MAP,
  };
}
